import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtEuro } from "@/lib/format";
import { BarChart3, ShoppingCart, Tags, Upload, FileDown } from "lucide-react";
import { KpiTab } from "@/components/bizstats/KpiTab";
import { SpendTab } from "@/components/bizstats/SpendTab";
import { PricesTab } from "@/components/bizstats/PricesTab";
import { ImportTab } from "@/components/bizstats/ImportTab";
import { useAuth } from "@/context/AuthContext";

const TABS = [["kpi", "KPIs & Προϋπολογισμός", BarChart3], ["spend", "Αγορές & Δαπάνες", ShoppingCart],
  ["prices", "Τιμές ειδών", Tags], ["import", "Εισαγωγή SoftOne", Upload]];

export default function BusinessStats() {
  const { user } = useAuth();
  const [tab, setTab] = useState("kpi");
  const [year, setYear] = useState(new Date().getFullYear());
  const canImport = ["owner", "hr"].includes(user?.role);
  const { data: imports = [] } = useQuery({ queryKey: ["erp-imports"], queryFn: async () => (await api.get("/erp/imports")).data });
  const last = imports[0];
  return (
    <div className="space-y-6" data-testid="business-stats-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Back office</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Στατιστικά & KPIs</h1>
          <p className="text-[#6F6A60] text-sm mt-1">Όλα τα ποσά είναι καθαρά, χωρίς ΦΠΑ.
            {last && <span data-testid="bs-last-import"> Τελευταία εισαγωγή SoftOne: {new Date(last.at).toLocaleDateString("el-GR")} · {({ purchases: "Αγορές", expenses: "Δαπάνες", items: "Αγορές αναλυτικά" })[last.kind]} ({fmtEuro(last.net_total)}).</span>}</p>
        </div>
        <div className="flex flex-wrap gap-3">
        {canImport && (
          <a data-testid="bs-softone-pdf" href="/downloads/SoftOne_Access_Request_TAF.pdf" download target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold text-[#45423C] hover:border-[#45423C]">
            <FileDown className="h-4 w-4" /> Αίτημα πρόσβασης SoftOne (PDF)
          </a>
        )}
        <select data-testid="bs-year" value={year} onChange={(e) => setYear(Number(e.target.value))}
          className="rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm outline-none focus:border-[#45423C]">
          {[2025, 2026, 2027, 2028].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {TABS.filter(([k]) => k !== "import" || canImport).map(([k, label, Icon]) => (
          <button key={k} data-testid={`bs-tab-${k}`} onClick={() => setTab(k)}
            className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === k ? "bg-[#45423C] text-white" : "bg-white border border-[#E3DED4] text-[#45423C] hover:border-[#45423C]"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>
      {tab === "kpi" && <KpiTab year={year} />}
      {tab === "spend" && <SpendTab year={year} />}
      {tab === "prices" && <PricesTab year={year} />}
      {tab === "import" && canImport && <ImportTab />}
    </div>
  );
}
